import React, { useEffect, useMemo, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRight } from '@fortawesome/free-solid-svg-icons/faArrowRight';
import { useNavigate } from 'react-router-dom';
import { Alert, AppBar, Box, Button, Toolbar } from '@mui/material';

import {
  toggleSignIn,
  toggleCartModal,
  toggleAddress,
} from '../../../redux/legacy/reducers/ducks/legacy/MainDuck';
import {
  setCheckoutContinuation,
  setPendingCheckoutOnSignup,
} from '../../../redux/legacy/reducers/ducks/legacy/OrderDuck';
import GuestCheckoutForm from '../../input/GuestCheckoutForm/GuestCheckoutForm';
import { routeNames } from '../../../util/Constants';
import useTotalCartPrice from '../../../util/hooks/UseCart';
import useSetCart from '../../../hooks/legacy/useSetCart';
import { useUser } from '../../../hooks/useUser';
import CartContent from '../CartContent';

import styles from './CartPanel.style';
import useClasses from '../../../hooks/legacy/useClasses';
import OrderTimeControl from '../OrderTimeControl/OrderTimeControl';
import useOrderTiming from '../../../hooks/useOrderTiming';

function CartPanel() {
  const classes = useClasses(styles);
  const {
    canContinue,
    requiresConfirmation,
    key: timingContext,
  } = useOrderTiming();
  const navigate = useNavigate();
  const { userData } = useUser();

  const dispatch = useDispatch();
  const { subtotalPrice, feesArray } = useTotalCartPrice();
  const [isPurchaseEnabled, setIsPurchaseEnabled] = useState(false);
  const [onCheckoutStart, setOnCheckoutStart] = useState(false);
  useEffect(() => {
    setOnCheckoutStart(false);
  }, [timingContext]);
  const [showGuestForm, setShowGuestForm] = useState(false);
  const panelRef = useRef(null);
  const [panelHeight, setPanelHeight] = useState(0);
  useEffect(() => {
    if (!panelRef.current || typeof ResizeObserver === 'undefined')
      return undefined;
    const observer = new ResizeObserver(([entry]) =>
      setPanelHeight(entry.target.getBoundingClientRect().height)
    );
    observer.observe(panelRef.current);
    return () => observer.disconnect();
  }, [showGuestForm]);
  useEffect(() => {
    if (requiresConfirmation) {
      setOnCheckoutStart(false);
      setShowGuestForm(false);
    }
  }, [requiresConfirmation]);
  const [sessionExpired, setSessionExpired] = useState(false);
  const [pendingSignInResume, setPendingSignInResume] = useState(false);
  const [
    checkoutReady,
    addressNeededCount,
    setCartFailedCount,
    isCheckoutCurrent,
  ] = useSetCart(onCheckoutStart);

  const {
    orderType,
    minimums,
    cartItems,
    updatingCartItems,
    menuItems,
    address,
    checkoutContinuation,
    isCartModalOpen,
    isSignInOpen,
  } = useSelector(({ order, restaurant, address: selectedAddress, main }) => ({
    orderType: order?.type,
    minimums: order.minimums,
    cartItems: order.cartItems,
    updatingCartItems: order.updatingCartItems,
    menuItems: restaurant?.selectedRestaurantMenu?.menuItems,
    address: selectedAddress?.selectedAddress,
    checkoutContinuation: order.checkoutContinuation,
    isCartModalOpen: main.isCartModalOpen,
    isSignInOpen: main.isSignInOpen,
  }));

  const minOrderPrice = useMemo(
    () => minimums?.[orderType.value] || 0,
    [minimums, orderType]
  );
  const isMinOrderPrice = useMemo(
    () => minOrderPrice <= subtotalPrice,
    [minOrderPrice, subtotalPrice]
  );
  const isDelivery = useMemo(
    () => orderType?.value === 'delivery',
    [orderType]
  );

  useEffect(() => {
    const isPurchase =
      !updatingCartItems.length &&
      isMinOrderPrice &&
      cartItems.length &&
      Object.keys(menuItems || {}).length;
    setIsPurchaseEnabled(isPurchase);
  }, [updatingCartItems, isMinOrderPrice, cartItems, menuItems]);

  useEffect(() => {
    if (checkoutReady && isCheckoutCurrent() && canContinue()) {
      navigate(routeNames.checkout);
    }
  }, [checkoutReady]);

  useEffect(() => {
    if (addressNeededCount > 0) {
      setOnCheckoutStart(false);
    }
  }, [addressNeededCount]);

  useEffect(() => {
    if (setCartFailedCount > 0) {
      setOnCheckoutStart(false);
      if (!userData) {
        setSessionExpired(true);
      }
    }
  }, [setCartFailedCount]);

  useEffect(() => {
    if (userData && sessionExpired) {
      setSessionExpired(false);
    }
  }, [userData, sessionExpired]);

  useEffect(() => {
    if (userData && pendingSignInResume) {
      setPendingSignInResume(false);
      if (canContinue()) setOnCheckoutStart(true);
    }
  }, [userData, pendingSignInResume]);

  useEffect(() => {
    if (!isSignInOpen && pendingSignInResume && !userData) {
      setPendingSignInResume(false);
    }
  }, [isSignInOpen, pendingSignInResume, userData]);

  useEffect(() => {
    if (checkoutContinuation && !isCartModalOpen) {
      dispatch(setCheckoutContinuation(false));
      if (canContinue()) setOnCheckoutStart(true);
    }
  }, [checkoutContinuation, isCartModalOpen]);

  const onCheckoutStartClick = (event) => {
    if (!canContinue()) {
      event.currentTarget
        .closest('[data-test-id="cart-panel"]')
        ?.querySelector('[data-test-id="change-order-time"]')
        ?.click();
      return;
    }
    if (isDelivery && !address) {
      dispatch(toggleAddress(true));
    } else if (userData) {
      setOnCheckoutStart(true);
    } else {
      setShowGuestForm(true);
    }
  };

  const handleGuestSignInClick = () => {
    setShowGuestForm(false);
    dispatch(setPendingCheckoutOnSignup(true));
    dispatch(toggleSignIn(true));
  };

  useEffect(() => {
    if (userData && showGuestForm) {
      setShowGuestForm(false);
    }
  }, [userData, showGuestForm]);

  if (showGuestForm) {
    return (
      <>
        <Box sx={{ height: panelHeight }} aria-hidden="true" />
        <AppBar
          ref={panelRef}
          position="fixed"
          color="primary"
          sx={{
            top: 'auto',
            bottom: 0,
            backgroundColor: 'white',
            height: 'auto',
            maxHeight: '80vh',
            overflowY: 'auto',
            pb: 'env(safe-area-inset-bottom)',
          }}
        >
          <GuestCheckoutForm
            isDelivery={isDelivery}
            onSignInClick={handleGuestSignInClick}
          />
        </AppBar>
      </>
    );
  }

  return (
    <>
      <Box sx={{ height: panelHeight }} aria-hidden="true" />
      <AppBar
        ref={panelRef}
        data-test-id="cart-panel"
        position="fixed"
        color="primary"
        sx={{
          top: 'auto',
          bottom: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'stretch',
          justifyContent: 'flex-end',
          backgroundColor: 'white',
          height: 'auto',
          pb: 'env(safe-area-inset-bottom)',
          maxHeight: '60dvh',
          overflowY: 'auto',
        }}
      >
        <OrderTimeControl showNotice={false} />
        {sessionExpired && (
          <Alert
            severity="warning"
            sx={{ borderRadius: 0, py: 0.5 }}
            action={
              <Button
                color="warning"
                size="small"
                variant="outlined"
                data-test-id="session-expired-sign-in"
                onClick={() => {
                  setSessionExpired(false);
                  setPendingSignInResume(true);
                  dispatch(toggleSignIn(true));
                }}
              >
                Sign In
              </Button>
            }
          >
            Session expired — please sign in to continue.
          </Alert>
        )}
        <Toolbar
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
            width: '100%',
            height: '70px',
            minHeight: '70px',
            pl: 2,
            pr: 3,
          }}
        >
          <Box>
            <CartContent
              variant="outlined"
              priceArray={feesArray}
              count={cartItems.length}
              cartAction={() => dispatch(toggleCartModal())}
              cartItems={cartItems}
            />
          </Box>
          <Box>
            <Button
              onClick={onCheckoutStartClick}
              variant="contained"
              data-test-id="checkout-link"
              disabled={!isPurchaseEnabled || onCheckoutStart}
              size="large"
            >
              {requiresConfirmation ? 'Confirm time' : 'Go to Checkout'}
              <span className={classes.checkoutArrow}>
                <FontAwesomeIcon
                  icon={faArrowRight}
                  className={classes.checkoutArrowIcon}
                />
              </span>
            </Button>
          </Box>
        </Toolbar>
      </AppBar>
    </>
  );
}

export default CartPanel;
